import {
  ADDITIONAL_CLEARANCE_TIERS,
  type CountryCode,
  PRODUCT_TURNAROUNDS,
  SCOPES,
  SCRIPT_PRODUCTION_TYPES,
  type Scope,
  type ScriptAnalysis,
  type ScriptProductionType,
  TIME_PERIODS,
  type TimePeriod,
  type TurnaroundDays,
  VERSION_PACKS,
} from '@noconflict/contracts';
import { PlusIcon, XIcon } from 'lucide-react';
import { type ReactNode, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../../shared/i18n/format';
import { Button } from '../../../shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shared/ui/card';
import { Checkbox } from '../../../shared/ui/checkbox';
import { CountrySelect } from '../../../shared/ui/country-select';
import { Field } from '../../../shared/ui/field';
import { Input as TextInput } from '../../../shared/ui/input';
import { Label } from '../../../shared/ui/label';
import { RadioCards } from '../../../shared/ui/radio-cards';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { Textarea } from '../../../shared/ui/textarea';
import type { ClearanceTier, ScriptDraft, VersionPack } from './model';

/** What a turnaround card says: when to order by, and when the report would arrive. */
export type TurnaroundEstimate = { cutoffText: string; deliveredText: string };

/** Slowest (cheapest) first, as the price list reads. */
const TURNAROUNDS = [...PRODUCT_TURNAROUNDS.script].sort((a, b) => b - a);
const MAX_CITIES = 10;

/** A group of controls under one label, for the multi-choice fields a single `Field` cannot name. */
function Group({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset would restyle the legend; the group role says the same
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-help`}
      className="grid gap-2"
    >
      <p id={`${id}-label`} className="text-sm font-bold text-heading">
        {label}
      </p>
      {children}
      {description ? (
        <p id={`${id}-help`} className="text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The script clearance order form, shown once the script has been read. The fields the script could answer
 * arrive filled in and every one can be changed; the length cannot, because it is the script's own. Each choice
 * with consequences is a set of radio cards that say what it means. Nothing is submitted here: each field saves
 * itself (`useScriptDraft`), and the price summary beside the form holds the submit button.
 */
export function ScriptOrderForm({
  draft,
  analysis,
  filename,
  scriptActions,
  estimates,
  referenceRequired,
}: {
  draft: ScriptDraft;
  /** The ready analysis of the project's script: its word count and length band. */
  analysis: ScriptAnalysis;
  /** What the uploaded file was called. */
  filename: string | null;
  /** Replace and remove. */
  scriptActions: ReactNode;
  /** Delivery estimates by turnaround; empty while the holidays load. */
  estimates: Map<number, TurnaroundEstimate>;
  referenceRequired: boolean;
}) {
  const { t, i18n } = useTranslation('order');
  const { formatNumber } = useFormat();
  const typeId = useId();
  const noCountryId = useId();
  const { values, set, commit, save } = draft;
  const episode = values.productionType === 'tv_episode';
  const countryNames = useMemo(
    () => new Intl.DisplayNames([i18n.language], { type: 'region' }),
    [i18n.language],
  );
  const countryName = (code: CountryCode) => countryNames.of(code) ?? code;
  const words = analysis.word_count ?? 0;
  const setCity = (index: number, patch: Partial<(typeof values.cities)[number]>) =>
    values.cities.map((city, i) => (i === index ? { ...city, ...patch } : city));

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('script.file.title')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          {filename ? <p className="font-bold break-words text-heading">{filename}</p> : null}
          <dl className="grid gap-1">
            <dt className="text-sm text-muted-foreground">{t('script.length.label')}</dt>
            <dd className="font-bold text-heading">
              {analysis.length
                ? t('script.length.summary', {
                    band: t(`script.length.bands.${analysis.length}`),
                    words: t('script.length.words', {
                      count: words,
                      formatted: formatNumber(words),
                    }),
                  })
                : null}
            </dd>
            <dd className="text-sm text-muted-foreground">
              {analysis.length ? t(`script.length.ranges.${analysis.length}`) : null}{' '}
              {t('script.length.locked')}
            </dd>
          </dl>
          {scriptActions}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('form.details')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="grid gap-2">
            <Label htmlFor={typeId}>{t('form.productionType')}</Label>
            <Select
              value={values.productionType ?? ''}
              onValueChange={(v) => commit({ productionType: v as ScriptProductionType })}
            >
              <SelectTrigger id={typeId} aria-required="true">
                <SelectValue placeholder={t('form.productionTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {SCRIPT_PRODUCTION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`form.productionTypes.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field
            label={episode ? t('script.form.showTitle') : t('form.title')}
            description={episode ? t('script.form.showTitleHelp') : t('script.form.titleHelp')}
          >
            <TextInput
              autoComplete="off"
              maxLength={200}
              required
              value={values.title}
              onChange={(e) => set({ title: e.target.value })}
              onBlur={save}
            />
          </Field>
          {episode ? (
            <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_12rem]">
              <Field label={t('script.form.episodeTitle')} description={t('script.form.optional')}>
                <TextInput
                  autoComplete="off"
                  maxLength={200}
                  value={values.episodeTitle}
                  onChange={(e) => set({ episodeTitle: e.target.value })}
                  onBlur={save}
                />
              </Field>
              <Field label={t('script.form.episodeNumber')} description={t('script.form.optional')}>
                <TextInput
                  autoComplete="off"
                  maxLength={50}
                  value={values.episodeNumber}
                  onChange={(e) => set({ episodeNumber: e.target.value })}
                  onBlur={save}
                />
              </Field>
            </div>
          ) : null}
          <Field label={t('script.form.producer')} description={t('script.form.producerHelp')}>
            <TextInput
              autoComplete="organization"
              maxLength={200}
              value={values.producer}
              onChange={(e) => set({ producer: e.target.value })}
              onBlur={save}
            />
          </Field>
          <Field label={t('script.form.authors')} description={t('script.form.authorsHelp')}>
            <TextInput
              autoComplete="off"
              value={values.authors}
              onChange={(e) => set({ authors: e.target.value })}
              onBlur={save}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('script.form.setting')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          <Group
            label={t('script.form.timePeriods')}
            description={t('script.form.timePeriodsHelp')}
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {TIME_PERIODS.map((period) => (
                <Label key={period} className="flex items-center gap-2 font-normal">
                  <Checkbox
                    checked={values.timePeriods.includes(period)}
                    onCheckedChange={(checked) =>
                      commit({
                        // kept in the catalogue's order, whatever order they were ticked in
                        timePeriods: TIME_PERIODS.filter((p: TimePeriod) =>
                          p === period ? checked === true : values.timePeriods.includes(p),
                        ),
                      })
                    }
                  />
                  {t(`script.form.periods.${period}`)}
                </Label>
              ))}
            </div>
          </Group>

          <Group label={t('script.form.countries')} description={t('script.form.countriesHelp')}>
            {values.noSpecificCountry ? null : (
              <>
                {values.countries.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {values.countries.map((code) => (
                      <li
                        key={code}
                        className="flex items-center gap-1 rounded-full border border-input bg-card py-1 pr-1 pl-3 text-sm"
                      >
                        {countryName(code)}
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('script.form.removeCountry', {
                            country: countryName(code),
                          })}
                          onClick={() =>
                            commit({ countries: values.countries.filter((c) => c !== code) })
                          }
                        >
                          <XIcon aria-hidden="true" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('script.form.noCountries')}</p>
                )}
                <CountrySelect
                  aria-label={t('script.form.addCountry')}
                  placeholder={t('script.form.addCountry')}
                  className="sm:max-w-xs"
                  onValueChange={(code) => {
                    if (!values.countries.includes(code))
                      commit({ countries: [...values.countries, code] });
                  }}
                />
              </>
            )}
            <div className="flex items-center gap-2">
              <Checkbox
                id={noCountryId}
                checked={values.noSpecificCountry}
                onCheckedChange={(checked) => commit({ noSpecificCountry: checked === true })}
              />
              <Label htmlFor={noCountryId} className="font-normal">
                {t('script.form.noSpecificCountry')}
              </Label>
            </div>
          </Group>

          <Group label={t('script.form.cities')} description={t('script.form.citiesHelp')}>
            {values.cities.map((city, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own, and a row is only removed by its own button
                key={index}
                className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_14rem_auto] sm:items-center"
              >
                <TextInput
                  aria-label={t('script.form.cityName', { number: index + 1 })}
                  autoComplete="off"
                  maxLength={100}
                  value={city.name}
                  onChange={(e) => set({ cities: setCity(index, { name: e.target.value }) })}
                  onBlur={save}
                />
                <CountrySelect
                  aria-label={t('script.form.cityCountry', { number: index + 1 })}
                  value={city.country ?? undefined}
                  onValueChange={(country) => commit({ cities: setCity(index, { country }) })}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('script.form.removeCity', { number: index + 1 })}
                  onClick={() => commit({ cities: values.cities.filter((_, i) => i !== index) })}
                >
                  <XIcon aria-hidden="true" />
                </Button>
              </div>
            ))}
            {values.cities.length < MAX_CITIES ? (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => set({ cities: [...values.cities, { name: '', country: null }] })}
                >
                  <PlusIcon aria-hidden="true" />
                  {t('script.form.addCity')}
                </Button>
              </div>
            ) : null}
          </Group>

          <Field label={t('script.form.basedOnReal')}>
            <RadioCards
              columns={2}
              value={values.basedOnReal === null ? null : values.basedOnReal ? 'yes' : 'no'}
              onValueChange={(v) => commit({ basedOnReal: v === 'yes' })}
              options={(['yes', 'no'] as const).map((answer) => ({
                value: answer,
                title: t(`review.${answer}`),
                description: t(`script.form.basedOnRealOptions.${answer}`),
              }))}
            />
          </Field>
          {values.basedOnReal ? (
            <Field
              label={t('script.form.basedOnRealNote')}
              description={t('script.form.basedOnRealNoteHelp')}
            >
              <Textarea
                maxLength={2000}
                value={values.basedOnRealNote}
                onChange={(e) => set({ basedOnRealNote: e.target.value })}
                onBlur={save}
              />
            </Field>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('form.choices')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          <Field label={t('form.scope')} description={t('script.form.scopeHelp')}>
            <RadioCards
              required
              columns={2}
              value={values.scope}
              onValueChange={(v) => commit({ scope: v as Scope })}
              options={SCOPES.map((scope) => ({
                value: scope,
                title: t(`scope.${scope}`),
                description: t(`script.form.scopeOptions.${scope}`),
              }))}
            />
          </Field>
          <Field label={t('form.turnaround')} description={t('form.turnaroundHelp')}>
            <RadioCards
              required
              value={String(values.turnaroundDays)}
              onValueChange={(v) => commit({ turnaroundDays: Number(v) as TurnaroundDays })}
              options={TURNAROUNDS.map((days) => {
                const estimate = estimates.get(days);
                return {
                  value: String(days),
                  title: t('turnaround', { count: days }),
                  description: estimate?.cutoffText ?? t('estimate.loading'),
                  ...(estimate ? { trailing: estimate.deliveredText } : {}),
                };
              })}
            />
          </Field>
          <Field label={t('script.form.clearances')} description={t('script.form.clearancesHelp')}>
            <RadioCards
              required
              columns={2}
              value={String(values.additionalClearances)}
              onValueChange={(v) => commit({ additionalClearances: Number(v) as ClearanceTier })}
              options={ADDITIONAL_CLEARANCE_TIERS.map((tier) => ({
                value: String(tier),
                title: t('script.form.clearanceTitle', { count: tier }),
                description: t(`script.form.clearanceOptions.${tier}`),
              }))}
            />
          </Field>
          <Field label={t('script.form.revisions')} description={t('script.form.revisionsHelp')}>
            <RadioCards
              required
              columns={2}
              value={String(values.versions)}
              onValueChange={(v) => commit({ versions: Number(v) as VersionPack })}
              options={VERSION_PACKS.map((pack) => ({
                value: String(pack),
                title:
                  pack === 0
                    ? t('script.form.revisionNone')
                    : t('script.form.revisionTitle', { count: pack }),
                description: t(`script.form.revisionOptions.${pack}`),
              }))}
            />
          </Field>
          {referenceRequired ? (
            <Field label={t('form.reference')} description={t('form.referenceHelp')}>
              <TextInput
                autoComplete="off"
                maxLength={80}
                required
                value={values.reference}
                onChange={(e) => set({ reference: e.target.value })}
                onBlur={save}
              />
            </Field>
          ) : null}
          <Field label={t('form.notes')} description={t('form.notesHelp')}>
            <Textarea
              maxLength={4000}
              value={values.notes}
              onChange={(e) => set({ notes: e.target.value })}
              onBlur={save}
            />
          </Field>
        </CardContent>
      </Card>
    </div>
  );
}
